import { seedMockAuthSession } from "@medme/auth"
import { Layout } from "@medme/layout"
import { Toaster } from "@medme/ui"
import type { QueryClient } from "@tanstack/react-query"
import {
	createRootRouteWithContext,
	Navigate,
	Outlet,
	redirect,
	useNavigate,
	useRouteContext,
} from "@tanstack/react-router"
import { useTranslation } from "react-i18next"
import { z } from "zod"
import type { AuthState } from "@/entities/auth"
import { initAuthAsync } from "@/entities/auth"
import { getUserProfileQueryOptions } from "@/entities/profile"
import { AuthError } from "@/shared/components/auth-error"
import { AuthLoading } from "@/shared/components/auth-loading"
import { AuthNotAuthenticated } from "@/shared/components/auth-not-authenticated"
import { UnknownError } from "@/shared/components/unknown-error"
import { Header } from "@/widgets/layout-header"
import { AppSidebar } from "@/widgets/layout-sidebar"
import { buildOAuthAuthorizeUrl } from "../config/app-config"
import { env } from "../config/env"

interface MyRouterContext {
	queryClient: QueryClient
	auth: AuthState
}

function getRedirectUrl(): string {
	return buildOAuthAuthorizeUrl()
}

export const Route = createRootRouteWithContext<MyRouterContext>()({
	validateSearch: z
		.object({
			code: z.string().optional(),
		})
		.describe("OAuth code"),
	beforeLoad: async ({ search, context }) => {
		const authResult = await initAuthAsync({
			oauthCode: search.code,
			queryClient: context.queryClient,
		})

		if (authResult.isAuthenticated) {
			await context.queryClient.ensureQueryData(getUserProfileQueryOptions())
		}

		// If OAuth code exists and authentication successful, redirect
		if (search.code && authResult.isAuthenticated) {
			throw redirect({
				to: "/key-metrics",
				replace: true,
			})
		}

		// Return updated context with auth
		return {
			auth: authResult,
		}
	},
	pendingComponent: () => {
		const { t } = useTranslation()
		return <AuthLoading loadingText={t("auth.loading")} />
	},
	errorComponent: ({ error, reset }) => {
		const context = useRouteContext({ from: "__root__" })
		const auth = context.auth
		const navigate = useNavigate()

		// Handle authentication errors
		if (auth?.error) {
			return (
				<AuthError
					message={auth.error.message}
					onRetry={() => {
						navigate({ to: "/" })
						reset()
					}}
				/>
			)
		}

		// Handle other errors
		return (
			<UnknownError
				error={error}
				reset={() => {
					navigate({ to: "/", replace: true })
					reset()
				}}
			/>
		)
	},
	notFoundComponent: () => {
		return <Navigate to="/key-metrics" />
	},
	component: () => {
		const { t } = useTranslation()
		const context = useRouteContext({ from: "__root__" })
		const auth = context.auth

		// Show not authenticated component if not authenticated
		if (!auth?.isAuthenticated) {
			return (
				<AuthNotAuthenticated
					title={t("auth.not-authenticated.title")}
					message={t("auth.not-authenticated.description")}
					authUrl={getRedirectUrl()}
					buttonText={t("auth.go-to-auth")}
					onAuthClick={
						env.VITE_ENABLE_MOCK
							? () => {
									seedMockAuthSession()
									window.location.assign("/")
								}
							: undefined
					}
				/>
			)
		}

		return (
			<Layout sidebar={<AppSidebar />} header={<Header />}>
				<Outlet />
				<Toaster />
				{/* <TanStackDevtools
					config={{
						position: "bottom-right",
					}}
					plugins={[TanStackRouterDevtools, TanStackQueryDevtools]}
				/> */}
			</Layout>
		)
	},
})
